import React, { useState } from 'react';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/ui/accordion';
import { Sparkles, FileText, DollarSign, Play, GraduationCap, Building2, Cpu, Users, Calendar, Phone, CreditCard, MessageCircle } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';
import BookingPopup from '@/components/BookingPopup';

const MobileMenuContent: React.FC = () => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [isBookingOpen, setIsBookingOpen] = useState(false);

  const bgColor = isDark ? '#1a1a1a' : '#FFFFFF';
  const hoverBg = isDark ? '#2a2a2a' : '#F5F5F2';
  const borderColor = isDark ? '#333333' : '#E5E5E5';
  const labelColor = isDark ? '#888888' : '#888888';
  const titleColor = isDark ? '#f4f2f1' : '#111111';
  const descColor = isDark ? '#999999' : '#555555';
  const iconColor = isDark ? '#888888' : '#888888';
  const gridLineColor = isDark ? '#444444' : '#d0d0d0';

  const iconBoxStyle: React.CSSProperties = {
    backgroundImage: `linear-gradient(to right, ${gridLineColor} 1px, transparent 1px), linear-gradient(to bottom, ${gridLineColor} 1px, transparent 1px)`,
    backgroundSize: '5px 5px',
    WebkitMaskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
    maskImage: 'radial-gradient(circle, black 0%, transparent 70%)',
  };

  return (
    <>
      <div className="w-full space-y-4" style={{ backgroundColor: bgColor }}>
        {/* Get Started Section */}
        <div className="pb-4 border-b" style={{ borderColor }}>
          <h3 className="text-[11px] font-medium uppercase tracking-wider mb-3" style={{ color: labelColor }}>
            Get Started
          </h3>
          <div className="space-y-2">
            <button 
              onClick={() => setIsBookingOpen(true)}
              className="flex items-center gap-3 p-2 rounded-md transition-colors w-full text-left"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                <Calendar className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
              </div>
              <span className="text-sm font-medium" style={{ color: titleColor }}>Book a Demo</span>
            </button>
            <a 
              href="tel:+18885056258" 
              className="flex items-center gap-3 p-2 rounded-md transition-colors"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                <Phone className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
              </div>
              <span className="text-sm font-medium" style={{ color: titleColor }}>Talk to Sales</span>
            </a>
            <a 
              href="/pricing" 
              className="flex items-center gap-3 p-2 rounded-md transition-colors"
              style={{ backgroundColor: 'transparent' }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
            >
              <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                <CreditCard className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
              </div>
              <span className="text-sm font-medium" style={{ color: titleColor }}>View Pricing</span>
            </a>
          </div>
        </div>

        <Accordion type="single" collapsible className="w-full">
          {/* Platform Section */}
          <AccordionItem value="platform" className="border-b" style={{ borderColor }}>
            <AccordionTrigger className="py-3 hover:no-underline">
              <h3 className="text-[11px] font-medium uppercase tracking-wider" style={{ color: labelColor }}>
                Platform
              </h3>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="space-y-2">
                <a 
                  href="/features" 
                  className="flex items-start gap-3 p-2 rounded-md transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                    <Sparkles className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium" style={{ color: titleColor }}>Features</h4>
                    <p className="text-xs mt-0.5" style={{ color: descColor }}>Full platform overview</p>
                  </div>
                </a>
                <a 
                  href="/ai" 
                  className="flex items-start gap-3 p-2 rounded-md transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                    <Cpu className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium" style={{ color: titleColor }}>AI</h4>
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: isDark ? '#f4f2f1' : '#111111', color: isDark ? '#111111' : '#f4f2f1' }}>
                        24/7
                      </span>
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: descColor }}>AI-powered automation</p>
                  </div>
                </a>
                <a 
                  href="/done-for-you" 
                  className="flex items-start gap-3 p-2 rounded-md transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                    <Users className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium" style={{ color: titleColor }}>Done-For-You</h4>
                    <p className="text-xs mt-0.5" style={{ color: descColor }}>Full-service marketing</p>
                  </div>
                </a>
                <a 
                  href="/agency-owners" 
                  className="flex items-start gap-3 p-2 rounded-md transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                    <Building2 className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium" style={{ color: titleColor }}>Agency Owners</h4>
                    <p className="text-xs mt-0.5" style={{ color: descColor }}>White-label solutions</p>
                  </div>
                </a>
                <a 
                  href="/platform-demos" 
                  className="flex items-start gap-3 p-2 rounded-md transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                    <Play className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium" style={{ color: titleColor }}>Platform Demos</h4>
                    <p className="text-xs mt-0.5" style={{ color: descColor }}>See it in action</p>
                  </div>
                </a>
                <a 
                  href="/pricing" 
                  className="flex items-start gap-3 p-2 rounded-md transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                    <DollarSign className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <h4 className="text-sm font-medium" style={{ color: titleColor }}>Pricing</h4>
                      <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ backgroundColor: '#0d7680', color: 'white' }}>
                        Free Trial
                      </span>
                    </div>
                    <p className="text-xs mt-0.5" style={{ color: descColor }}>Plans & pricing</p>
                  </div>
                </a>
              </div>
            </AccordionContent>
          </AccordionItem>

          {/* Resources Section */}
          <AccordionItem value="resources" className="border-b" style={{ borderColor }}>
            <AccordionTrigger className="py-3 hover:no-underline">
              <h3 className="text-[11px] font-medium uppercase tracking-wider" style={{ color: labelColor }}>
                Resources
              </h3>
            </AccordionTrigger>
            <AccordionContent className="pb-4">
              <div className="space-y-2">
                <a 
                  href="/free-marketing" 
                  className="flex items-start gap-3 p-2 rounded-md transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                    <Sparkles className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium" style={{ color: titleColor }}>Free Marketing</h4>
                    <p className="text-xs mt-0.5" style={{ color: descColor }}>Expert tips & proven tactics</p>
                  </div>
                </a>
                <a 
                  href="/help" 
                  className="flex items-start gap-3 p-2 rounded-md transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                    <MessageCircle className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium" style={{ color: titleColor }}>Help Center</h4>
                    <p className="text-xs mt-0.5" style={{ color: descColor }}>Find instant answers</p>
                  </div>
                </a>
                <a 
                  href="/blog" 
                  className="flex items-start gap-3 p-2 rounded-md transition-colors"
                  style={{ backgroundColor: 'transparent' }}
                  onMouseEnter={(e) => e.currentTarget.style.backgroundColor = hoverBg}
                  onMouseLeave={(e) => e.currentTarget.style.backgroundColor = 'transparent'}
                >
                  <div className="w-8 h-8 flex items-center justify-center flex-shrink-0" style={iconBoxStyle}>
                    <FileText className="w-4 h-4" style={{ color: iconColor }} strokeWidth={1.5} />
                  </div>
                  <div>
                    <h4 className="text-sm font-medium" style={{ color: titleColor }}>Articles</h4>
                    <p className="text-xs mt-0.5" style={{ color: descColor }}>Latest insights & updates</p>
                  </div>
                </a>
              </div>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <BookingPopup isOpen={isBookingOpen} onClose={() => setIsBookingOpen(false)} />
    </>
  );
};

export default MobileMenuContent;